import { type Board, type Reservation } from './types';
import { BoardColumn, getColumnValue, getColumnNumber, reservationEncaisse } from './boardColumns';

/**
 * Export du tableau en vrai classeur Excel (maquette 16e).
 *
 * Un `.xlsx` écrit avec ExcelJS, pas un CSV renommé : le fichier porte un titre fusionné, des en-têtes
 * colorés, un zébrage, des formats de nombre, un filtre automatique et la ligne d'en-tête figée.
 *
 * Les totaux sont de VRAIES formules (`=SUM(G5:G10)`), pas des valeurs calculées en JavaScript :
 * corriger un montant dans le fichier recalcule tout le reste, ce qu'un nombre figé ne ferait pas.
 *
 * ExcelJS est chargé à la demande — c'est une grosse bibliothèque, elle n'a pas à peser sur le
 * démarrage de l'app pour une fonction qu'on utilise une fois par service.
 */

const HEADER_FILL = 'FFC1D5AF';
const HEADER_BORDER = 'FFA9C092';
const TITLE_FILL = 'FF1F3A4A';
const ZEBRA_FILL = 'FFF7F9F4';
const TOTALS_FILL = 'FFF1F2F3';
const MONEY_FORMAT = '# ##0,00 €';

/** Colonnes exportées : celles affichées, plus le reste dû et l'origine. */
interface ExportColumn {
    header: string;
    width: number;
    align: 'left' | 'center' | 'right';
    /** Colonne dont le pied porte une somme. */
    summed: boolean;
    money: boolean;
    value: (r: Reservation) => string | number;
}

const buildColumns = (board: Board, includeOrigin: boolean): ExportColumn[] => {
    const cols: ExportColumn[] = [
        { header: 'Client', width: 28, align: 'left', summed: false, money: false,
          value: (r) => `${r.lastName} ${r.firstName}`.trim() },
        { header: 'Téléphone', width: 18, align: 'left', summed: false, money: false, value: (r) => r.phone || '' },
    ];

    board.columns.filter((c) => c.visible && c.id !== 'client').forEach((col: BoardColumn) => {
        cols.push({
            header: col.label,
            width: col.type === 'montant' ? 14 : col.type === 'personne' ? 20 : 12,
            align: col.type === 'montant' ? 'right' : col.type === 'nombre' || col.type === 'heure' ? 'center' : 'left',
            summed: col.type === 'montant' || col.type === 'nombre',
            money: col.type === 'montant',
            value: (r) => (col.type === 'montant' || col.type === 'nombre'
                ? getColumnNumber(r, col)
                : String(getColumnValue(r, col) ?? '')),
        });
    });

    cols.push({
        header: 'Encaissé', width: 14, align: 'right', summed: true, money: true,
        value: (r) => reservationEncaisse(r, board.columns),
    });
    cols.push({
        header: 'CA théorique', width: 14, align: 'right', summed: true, money: true,
        value: (r) => r.adults * r.tarifAdult + r.children * r.tarifChild,
    });
    cols.push({
        header: 'Reste dû', width: 14, align: 'right', summed: true, money: true,
        value: (r) => Math.max(0, r.adults * r.tarifAdult + r.children * r.tarifChild - reservationEncaisse(r, board.columns)),
    });

    if (includeOrigin) {
        cols.push({
            header: 'Origine', width: 16, align: 'left', summed: false, money: false,
            value: (r) => (r.source && r.source !== 'manual' ? r.source : 'Saisie à la main'),
        });
    }
    return cols;
};

/** « Fête nationale » + 2026-07-14 → « fete-nationale-2026-07-14.xlsx ». */
export const excelFileName = (board: Board): string => {
    const slug = (board.title || 'service').trim().toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'service';
    return `${slug}-${board.serviceDate || 'sans-date'}.xlsx`;
};

export async function exportBoardToExcel({
    board, reservations, companyName, includeOrigin = true, withFormulas = true,
}: {
    board: Board;
    reservations: Reservation[];
    companyName: string;
    includeOrigin?: boolean;
    withFormulas?: boolean;
}) {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = companyName;
    workbook.created = new Date();

    const dateLong = board.serviceDate
        ? new Date(`${board.serviceDate}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
        : '';
    const sheetName = board.serviceDate
        ? new Date(`${board.serviceDate}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
        : 'Service';

    const sheet = workbook.addWorksheet(sheetName, {
        views: [{ state: 'frozen', ySplit: 4 }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    const columns = buildColumns(board, includeOrigin);
    const covers = reservations.reduce((s, r) => s + r.adults + r.children, 0);

    // La grille commence en colonne B : la colonne A sert de marge, comme dans la maquette.
    sheet.getColumn(1).width = 3;
    columns.forEach((col, i) => { sheet.getColumn(i + 2).width = col.width; });

    const firstCol = 2;
    const lastCol = columns.length + 1;
    const colLetter = (n: number) => sheet.getColumn(n).letter;

    // Ligne 1 — titre fusionné
    sheet.mergeCells(1, firstCol, 1, lastCol);
    const titleCell = sheet.getCell(1, firstCol);
    titleCell.value = `${(board.title || '').toUpperCase()}${dateLong ? ` — ${dateLong.toUpperCase()}` : ''}`;
    titleCell.font = { bold: true, size: 14.5, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TITLE_FILL } };
    sheet.getRow(1).height = 26;

    // Ligne 2 — établissement, volumétrie, horodatage de l'export
    sheet.mergeCells(2, firstCol, 2, lastCol);
    const subCell = sheet.getCell(2, firstCol);
    subCell.value = `${companyName} · ${reservations.length} réservation${reservations.length > 1 ? 's' : ''} · ${covers} couverts · export du ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    subCell.font = { size: 11.5, color: { argb: 'FF4A5058' } };
    subCell.alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getRow(2).height = 18;

    // Ligne 3 laissée vide : la respiration entre le cartouche et le tableau.

    // Ligne 4 — en-têtes
    const headerRow = sheet.getRow(4);
    columns.forEach((col, i) => {
        const cell = headerRow.getCell(firstCol + i);
        cell.value = col.header;
        cell.font = { bold: true, size: 10.5, color: { argb: 'FF1B2A12' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
        cell.alignment = { horizontal: col.align, vertical: 'middle' };
        cell.border = {
            top: { style: 'thin', color: { argb: HEADER_BORDER } },
            left: { style: 'thin', color: { argb: HEADER_BORDER } },
            bottom: { style: 'thin', color: { argb: HEADER_BORDER } },
            right: { style: 'thin', color: { argb: HEADER_BORDER } },
        };
    });
    headerRow.height = 20;

    // Lignes 5 → N — les réservations, zébrées une sur deux
    const firstDataRow = 5;
    reservations.forEach((r, index) => {
        const row = sheet.getRow(firstDataRow + index);
        columns.forEach((col, i) => {
            const cell = row.getCell(firstCol + i);
            cell.value = col.value(r);
            cell.alignment = { horizontal: col.align };
            if (col.money) cell.numFmt = MONEY_FORMAT;
            if (index % 2 === 1) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA_FILL } };
            }
        });
    });

    const lastDataRow = firstDataRow + Math.max(reservations.length, 1) - 1;

    // Ligne N+1 — totaux, en vraies formules
    const totalsRowIndex = lastDataRow + 1;
    const totalsRow = sheet.getRow(totalsRowIndex);
    columns.forEach((col, i) => {
        const cell = totalsRow.getCell(firstCol + i);
        if (i === 0) cell.value = 'TOTAUX';
        else if (col.summed && reservations.length > 0) {
            const letter = colLetter(firstCol + i);
            cell.value = withFormulas
                ? { formula: `SUM(${letter}${firstDataRow}:${letter}${lastDataRow})` } as any
                : reservations.reduce((s, r) => s + Number(col.value(r) || 0), 0);
        }
        cell.font = { bold: true, size: 11.5, color: { argb: 'FF1F3A4A' } };
        cell.alignment = { horizontal: i === 0 ? 'left' : col.align };
        if (col.money) cell.numFmt = MONEY_FORMAT;
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTALS_FILL } };
        cell.border = { top: { style: 'medium', color: { argb: TITLE_FILL } } };
    });

    // Filtre automatique sur toute la plage, en-têtes compris
    sheet.autoFilter = {
        from: { row: 4, column: firstCol },
        to: { row: lastDataRow, column: lastCol },
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = excelFileName(board);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Révocation différée : sur Safari, révoquer immédiatement annule le téléchargement en cours.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
